import CloseIcon from "@mui/icons-material/Close";
import { Grid, IconButton, Stack, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxExpansion from "components/UI/AcxExpansion";
import * as AYSDialog from "components/UI/AYSDialog";
import AcxDrawer from "components/UI/Drawer/AcxDrawer";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import React from "react";
import { AuthStore } from "stores/AuthStore";
import { OrganizationServiceHierarchyStore } from "../stores/OrganizationServiceHierarchyStore";
import {
    diffTierProperties,
    formatTierFieldValue,
    TierPropertyChange,
} from "../utils/tierPropertyDiff";
import OsmHierarchyMetadataLayout from "./OsmHierarchyMetadataLayout";
import OsmPropertiesLayout from "./OsmPropertiesLayout";

type Props = {
    osmStore: OrganizationServiceHierarchyStore;
    authStore: AuthStore;
};

const OrganizationServiceHierarchyDrawer: React.FC<
    React.PropsWithChildren<React.PropsWithChildren<Props>>
> = observer(({ osmStore, authStore }) => {
    const [originalTier, setOriginalTier] = React.useState(
        osmStore.activeTier ? _.cloneDeep(osmStore.activeTier) : undefined,
    );
    const [pendingChanges, setPendingChanges] = React.useState<
        TierPropertyChange[]
    >([]);
    const [showConfirmChanges, setShowConfirmChanges] = React.useState(false);
    const hasEditPerms = authStore.canUserEdit("Service Hierarchy");

    React.useEffect(() => {
        if (osmStore.showEditTierDrawer && osmStore.activeTier) {
            setOriginalTier(_.cloneDeep(osmStore.activeTier));
            setPendingChanges([]);
            setShowConfirmChanges(false);
        }
    }, [osmStore.activeTier, osmStore.showEditTierDrawer]);

    const liveChanges =
        originalTier && osmStore.activeTier
            ? diffTierProperties(originalTier, osmStore.activeTier)
            : [];
    const hasChanges = liveChanges.length > 0;
    const shouldApplyToChildren =
        osmStore.applyTherapeuticAreasToChildren &&
        (osmStore.activeTier?.descendantCount ?? 0) > 0;
    const canSave = !!osmStore.activeTier?.id && (hasChanges || shouldApplyToChildren);

    const handleCancel = () => {
        osmStore.setApplyTherapeuticAreasToChildren(false);
        osmStore.setShowEditTierDrawer(false);
    };

    const handleSave = () => {
        if (!osmStore.activeTier?.id || !canSave) return;
        setPendingChanges(liveChanges);
        setShowConfirmChanges(true);
    };

    const handleConfirmChanges = () => {
        setShowConfirmChanges(false);
        osmStore.setEndpointFired(true, "Update");
    };

    return (
        <AcxDrawer
            offsetPx={30}
            collapsible="full"
            anchor="right"
            open={osmStore.showEditTierDrawer}
            content={
                <Grid
                    container
                    direction="column"
                    wrap="nowrap"
                    sx={{
                        minHeight: "calc(100vh - 30px)",
                        padding: "2rem",
                        paddingTop: "calc(2rem + 16px)",
                        width: "625px",
                    }}
                >
                    <IconButton
                        aria-label="close"
                        sx={{
                            position: "absolute",
                            top: 70,
                            color: "#A1A1AA",
                            right: 8,
                        }}
                        onClick={handleCancel}
                        size="large"
                    >
                        <CloseIcon sx={{ height: 20, width: 20 }} />
                    </IconButton>

                    <Grid item sx={{ paddingBottom: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Tier Settings
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                            {osmStore.activeTier?.name ?? ""}
                        </Typography>
                    </Grid>

                    <Grid item xs sx={{ borderTop: "1px solid #E5E7EB" }}>
                        <AcxExpansion
                            expanded={false}
                            header="Tier Properties"
                            rootStyle={{ padding: "1rem 0" }}
                            body={
                                <OsmPropertiesLayout
                                    osmStore={osmStore}
                                    authStore={authStore}
                                />
                            }
                        />

                        <AcxExpansion
                            expanded={true}
                            header="Hierarchy Metadata"
                            rootStyle={{
                                borderTop: "1px solid #E5E7EB",
                                padding: "1rem 0",
                            }}
                            body={
                                <OsmHierarchyMetadataLayout
                                    osmStore={osmStore}
                                    authStore={authStore}
                                />
                            }
                        />
                    </Grid>

                    <Grid
                        container
                        item
                        justifyContent="flex-end"
                        columnGap={1}
                        sx={{
                            borderTop: "1px solid #E5E7EB",
                            paddingTop: 2,
                        }}
                    >
                        <AcxButton
                            disabled={osmStore.isLoading}
                            onClick={handleCancel}
                        >
                            Cancel
                        </AcxButton>

                        <AcxButton
                            disabled={!hasEditPerms || !canSave}
                            loading={osmStore.isLoading}
                            onClick={handleSave}
                            color="primary"
                        >
                            Save
                        </AcxButton>
                    </Grid>

                    <AYSDialog.Container
                        isOpen={showConfirmChanges}
                        onClose={() => setShowConfirmChanges(false)}
                        id="confirm-tier-property-changes"
                    >
                        <AYSDialog.Title>
                            Confirm changes to{" "}
                            {osmStore.activeTier?.name ?? "tier"}
                        </AYSDialog.Title>
                        <AYSDialog.Subtitle>
                            Review the changes below before saving.
                        </AYSDialog.Subtitle>
                        <AYSDialog.Content>
                            <Stack direction="column" rowGap={1.5}>
                                {pendingChanges.length === 0 && (
                                    <Typography variant="body2">
                                        No tier fields changed.
                                    </Typography>
                                )}
                                {pendingChanges.map((change) => (
                                    <Stack
                                        key={change.field}
                                        direction="column"
                                        rowGap={0.25}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {change.label}
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatTierFieldValue(
                                                change.field,
                                                change.before,
                                            )}{" "}
                                            →{" "}
                                            {formatTierFieldValue(
                                                change.field,
                                                change.after,
                                            )}
                                        </Typography>
                                    </Stack>
                                ))}
                                {shouldApplyToChildren && (
                                    <Typography variant="body2">
                                        The selected therapeutic areas will also
                                        be applied to{" "}
                                        {osmStore.activeTier?.descendantCount ??
                                            0}{" "}
                                        child nodes.
                                    </Typography>
                                )}
                            </Stack>
                        </AYSDialog.Content>
                        <AYSDialog.Actions>
                            <AcxButton
                                color="white"
                                onClick={() => setShowConfirmChanges(false)}
                            >
                                Cancel
                            </AcxButton>
                            <AcxButton
                                color="primary"
                                onClick={handleConfirmChanges}
                            >
                                Save Changes
                            </AcxButton>
                        </AYSDialog.Actions>
                    </AYSDialog.Container>
                </Grid>
            }
        />
    );
});

export default OrganizationServiceHierarchyDrawer;
